import * as React from "react";
import {FC, useEffect, useState} from "react";
import {useFormContext} from "react-hook-form";
import {Box, Grid, IconButton, Paper, Tab, Tabs} from "@mui/material";
import {BrickStatus} from "../../BrickMailType";
import {FormInputText} from "../../../../shop/wholesale/form/FormInputText";
import {FormInputColor} from "../../../../shop/wholesale/form/FormInputColor";
import {FormInputSelect} from "../../../../shop/wholesale/form/FormInputSelect";
import {FormInputHtml} from "../../../../shop/wholesale/form/FormInputHtml";
import {alignments} from "../../constants/MailConstants";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {BodyText, TitleText} from "../../../../../layout/Typography";

export const BrickStatusEditor: FC<{ brick: BrickStatus, onChange: (brick: BrickStatus) => void }> = ({
  brick,
  onChange
}) => {
  const [tab, setTab] = useState<'general' | 'blocks'>('general');
  const [blockKey, setBlockKey] = useState<string | null>(null);
  const formContext = useFormContext<BrickStatus>();
  const {reset, control, getValues, setValue} = formContext;

  useEffect(() => {
    reset(brick);
  }, [brick, reset]);

  const handleAddBlock = () => {
    const currentBlocks = getValues('blocks') || {};
    const newKey = prompt('Enter block key (e.g., REJECTED_DAMAGED):');

    if (newKey && !currentBlocks[newKey]) {
      const updatedBlocks = {
        ...currentBlocks,
        [newKey]: {
          text: '',
          color: brick.color || '#000000'
        }
      };

      // Update form values
      setValue('blocks', updatedBlocks);

      // Select the new block
      setBlockKey(newKey);
    }
  };

  const handleDeleteBlock = (key: string) => {
    if (window.confirm(`Are you sure you want to delete the block "${key}"?`)) {
      const currentBlocks = getValues('blocks') || {};
      const {[key]: _, ...remainingBlocks} = currentBlocks;

      // Update form values
      setValue('blocks', remainingBlocks);

      // Reset selected block if it was deleted
      if (blockKey === key) {
        setBlockKey(null);
      }
    }
  };

  return (
    <div>
      <Tabs value={tab}>
        <Tab value={'general'} label="General" onClick={() => setTab('general')}/>
        <Tab value={'blocks'} label="Blocks" onClick={() => setTab('blocks')}/>
      </Tabs>

      {tab === 'general' && (
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <FormInputSelect name="align" control={control} label="Align" options={alignments}/>
          </Grid>
          <Grid item xs={6}>
            <FormInputText name="margin" control={control} label="Margin"/>
          </Grid>
          <Grid item xs={6}>
            <FormInputText name="fontSize" control={control} label="Font Size"/>
          </Grid>
          <Grid item xs={6}>
            <FormInputText name="fontFamily" control={control} label="Font Family"/>
          </Grid>
          <Grid item xs={6}>
            <FormInputColor name="color" control={control} label="Text Color" defaultColor="#000000"/>
          </Grid>
          <Grid item xs={6}>
            <FormInputColor name="backgroundColor" control={control} label="Background Color"
                            defaultColor="#ffffff"/>
          </Grid>
          <Grid item xs={12}>
            <FormInputText name={"activation"} control={control} label={"Activation expression"}/>
          </Grid>
        </Grid>
      )}

      {tab === 'blocks' && (
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <Paper variant="outlined" style={{padding: '8px', height: '300px', overflowY: 'auto'}}>
              <TitleText type="h3" style={{fontSize: '14px', marginBottom: '8px'}}>Block Keys</TitleText>
              <div>
                {Object.keys(getValues('blocks') || {}).map((key) => (
                  <div
                    key={key}
                    style={{
                      cursor: 'pointer',
                      padding: '8px',
                      backgroundColor: blockKey === key ? '#f0f0f0' : 'transparent',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      borderBottom: '1px solid #eee'
                    }}
                    onClick={() => setBlockKey(key)}
                  >
                    <BodyText type="body1">{key}</BodyText>
                    {!['TITLE', 'DESCRIPTION', 'SUMTOTAL', 'VOUCHER', 'ITEM', 'OKAY', 'REJECTDEFAULT'].includes(key) && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteBlock(key);
                        }}
                      >
                        <DeleteIcon fontSize="small"/>
                      </IconButton>
                    )}
                  </div>
                ))}
              </div>
              <Box sx={{marginTop: '8px'}}>
                <IconButton
                  color="primary"
                  size="small"
                  onClick={handleAddBlock}
                >
                  <AddIcon/>
                </IconButton>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={9}>
            {blockKey && (
              <Paper variant="outlined" style={{padding: '16px', height: '300px', overflowY: 'auto'}}>
                <TitleText type="h3" style={{marginBottom: '16px'}}>{blockKey}</TitleText>
                <div key={`form-block-${blockKey}`}>
                  <FormInputHtml
                    height="100px"
                    name={`blocks.${blockKey}.text`}
                    label="Text Content"
                  />
                  <Grid container spacing={2} sx={{marginTop: '8px'}}>
                    <Grid item xs={6}>
                      <FormInputColor
                        name={`blocks.${blockKey}.color`}
                        label="Text Color"
                        defaultColor={brick.color || "#000000"}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <FormInputColor
                        name={`blocks.${blockKey}.backgroundColor`}
                        label="Background Color"
                        defaultColor={brick.backgroundColor || "#FFFFFF"}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <FormInputText
                        name={`blocks.${blockKey}.fontSize`}
                        label="Font Size"
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <FormInputText
                        name={`blocks.${blockKey}.fontFamily`}
                        label="Font Family"
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <FormInputText
                        name={`blocks.${blockKey}.fontWeight`}
                        label="Font Weight"
                      />
                    </Grid>
                  </Grid>
                </div>
              </Paper>
            )}
            {!blockKey && (
              <Paper
                variant="outlined"
                style={{
                  padding: '16px',
                  height: '300px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <BodyText type="body2" style={{color: '#666'}}>
                  Select a block to edit its properties
                </BodyText>
              </Paper>
            )}
          </Grid>
        </Grid>
      )}
    </div>
  );
}